import { AsyncPipe, NgIf } from '@angular/common';
import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { emailValidator } from '@lx/core/validators';
import { StepService } from '@lx/quote-tool/shared/services';
import { EnrolmentFormPayload, FormFieldObject, SimplifiedTellUsAboutYouSectionFormPayload, StudentDetails } from '@lx/quote-tool/shared/types';
import { DateOfBirthFieldComponent, DestinationComponentsModule, PhoneNumberFieldComponent } from '@lx/quote-tool/shared/ui/components';
import {
  BlockSpacesDirective,
  FormDataAutofillDetectorDirective,
  FormDataCustomComponentValidityDirective,
  FormDataDropdownValidityDirective,
  FormDataEmailValidityDirective,
  FormDataInputValidityDirective,
  FormDataPostcodeValidityDirective,
  FormSection,
  StudentDetailsLoggingDirective,
} from '@lx/quote-tool/shared/ui/directives';
import { EmailErrorPipe } from '@lx/quote-tool/shared/ui/pipes';
import { phoneAndPrefixValidator, splitPhoneDigits } from '@lx/quote-tool/shared/validators';
import { DEFAULT_ERROR_MESSAGE } from '@lx/shared/errors';
import { GoogleAnalyticsService } from '@lx/shared/google-analytics';
import { getLocale } from '@lx/shared/i18n';
import { ClickCounterDirective, ClickCounterService } from '@lx/shared/ui/directives';
import { FormFieldComponent } from '@lx/shared/ui/form-field';
import { getDateFromString, getDateStringFromDate, getToday } from '@lx/shared/utils';
import { InputTextModule } from 'primeng/inputtext';
import { BehaviorSubject } from 'rxjs';
import { first } from 'rxjs/operators';
import { PriceRequestFormGoogleAnalyticsEvent } from '../../../../../google-analytics';
import { PriceRequestFormStore } from '../../../../../store/store';
import { SectionTemplateComponent } from '../../../../sections/section-template/section-template.component';
import { TermsAndPromotionsFormSectionComponent } from '../../../../sections/terms-and-promotions/terms-and-promotions.component';

@Component({
  standalone: true,
  selector: 'lx-price-request-student-form',
  templateUrl: './price-request-student-form.component.html',
  styleUrls: ['./price-request-student-form.component.scss'],
  imports: [
    SectionTemplateComponent,
    FormFieldComponent,
    InputTextModule,
    ReactiveFormsModule,
    FormsModule,
    NgIf,
    TermsAndPromotionsFormSectionComponent,
    DestinationComponentsModule,
    FormDataDropdownValidityDirective,
    FormDataInputValidityDirective,
    FormDataEmailValidityDirective,
    DateOfBirthFieldComponent,
    PhoneNumberFieldComponent,
    FormDataAutofillDetectorDirective,
    EmailErrorPipe,
    ClickCounterDirective,
    FormDataPostcodeValidityDirective,
    StudentDetailsLoggingDirective,
    BlockSpacesDirective,
    AsyncPipe,
    FormDataCustomComponentValidityDirective,
  ],
  providers: [ClickCounterService],
})
export class PriceRequestStudentFormComponent extends FormSection<SimplifiedTellUsAboutYouSectionFormPayload> implements OnInit, OnChanges {
  // Number of columns of the form, by default it will be 2 just to be as similar as the
  // original price request form
  @Input() columns = 2;

  // The form title (it could be empty)
  @Input() title = '';

  // Flag to indicate if form have validation errors and if true, show the error message
  @Input() withValidationErrors: boolean;

  @Input() validationRequests = 0;

  @Output() validForm = new EventEmitter<boolean>();

  validStep$ = new BehaviorSubject<boolean>(false);
  withNationalityField = false;
  phoneCode = '';
  phoneNumber = '';
  preselectedDateOfBirth: Date | undefined;
  calendarMaxDate = getToday();
  calendarMinDate = new Date('01/01/1900');

  formFields:
    | {
        [K in keyof Partial<SimplifiedTellUsAboutYouSectionFormPayload>]: FormFieldObject<SimplifiedTellUsAboutYouSectionFormPayload[K]>;
      }
    | undefined;

  private readonly priceRequestStore = inject(PriceRequestFormStore);
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);
  private readonly locale = getLocale();
  private readonly destroyRef = inject(DestroyRef);

  constructor(stepService: StepService) {
    super(stepService);
  }

  ngOnInit(): void {
    // hard-coded locale due to being an experiment
    this.withNationalityField = this.locale === 'fr-FR';

    this.priceRequestStore.studentDetails$.pipe(first(), takeUntilDestroyed(this.destroyRef)).subscribe((studentDetails) => {
      this.initializeDataFromStoredStudentDetails(studentDetails);
      this.initFormFields(studentDetails);
      this.formData = this.getFormData();
    });

    this.validStep$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((validStep: boolean) => {
      if (validStep) {
        this.storeStudentDetails();
      }

      this.validForm.next(validStep);
    });
  }

  ngOnChanges({ withValidationErrors }: SimpleChanges) {
    if (withValidationErrors && withValidationErrors.currentValue) {
      this.recalculateFormValidation('all');
    }
  }

  /**
   * It validates the field whose key is passed as a parameter
   */
  validateField(key: keyof StudentDetails) {
    this.storeStudentDetails();
    this.formData[key].validate();
    this.validStep$.next(this.isFormValid());
  }

  onAutoFill(): void {
    this.recalculateFormValidation('preFilled');
  }

  /**
   * Sets the selected country in the form and validates the field
   */
  setCountryOfResidence(countryOfResidence: string) {
    const key = 'countryOfResidence';
    this.formData[key].value = countryOfResidence;
    this.validateField(key);
  }

  onSelectBirthDate(date: Date): void {
    const key = 'dateOfBirth';
    this.formData[key].value = getDateStringFromDate(date);
    this.validateField(key);
  }

  onSelectPhoneNumber(phoneNumber: string) {
    const key = 'phoneNumber';
    this.phoneCode = splitPhoneDigits(phoneNumber, 0);
    this.phoneNumber = splitPhoneDigits(phoneNumber, 1);
    this.formData[key].value = phoneNumber;
    this.validateField(key);
  }

  onSelectNationality(nationality: string): void {
    const key = 'nationality';
    this.formData[key].value = nationality;
    this.validateField(key);
  }

  updateAgreeToPromotions(consentMarketing: boolean): void {
    this.priceRequestStore.updateConsentMarketing(consentMarketing || false);
  }

  /**
   * Tracks the click event for the Google Analytics. It will only track the event if it
   * is the first time the users click on any of the fields. For the consecutive clicks, it will not track the event.
   */
  trackClickEvent(counter: number) {
    if (counter > 1) {
      return;
    }

    this.googleAnalyticsService.trackClickEvent(PriceRequestFormGoogleAnalyticsEvent.QuoteStep2);
  }

  protected getFormFields(): {
    [K in keyof Partial<SimplifiedTellUsAboutYouSectionFormPayload>]: FormFieldObject<SimplifiedTellUsAboutYouSectionFormPayload[K]>;
  } {
    return this.formFields;
  }

  protected getFieldsValues(fieldKeys: Array<keyof SimplifiedTellUsAboutYouSectionFormPayload>): Partial<SimplifiedTellUsAboutYouSectionFormPayload> {
    const values: Partial<SimplifiedTellUsAboutYouSectionFormPayload> = {};

    fieldKeys.forEach((key: keyof SimplifiedTellUsAboutYouSectionFormPayload) => {
      if (this.getFieldValueIfValid(key) !== undefined || this.getFormFields()[key].required) {
        values[key] = this.getFieldValueIfValid(key);
      }
    });

    return values;
  }

  protected storeStudentDetails() {
    const formValues = this.getFormValues() as StudentDetails;
    this.priceRequestStore.updateStudentDetails(formValues);
  }

  private recalculateFormValidation(formKey?: keyof EnrolmentFormPayload | 'all' | 'preFilled') {
    Object.entries(this.formData).forEach(([key, formField]) => {
      formField.recalculateRequired();
      if (formKey === 'all' || key === formKey) {
        formField.validate();
      } else if (formKey === 'preFilled' && formField.value) {
        formField.validate();
      }
    });
  }

  private initFormFields(studentDetails: StudentDetails) {
    this.formFields = {
      firstName: {
        value: studentDetails?.firstName || '',
        validator: (firstName) => firstName && firstName.length <= 80,
        errorMessage: DEFAULT_ERROR_MESSAGE,
        required: true,
      },
      lastName: {
        value: studentDetails?.lastName || '',
        validator: (lastName) => lastName && lastName.length <= 40,
        errorMessage: DEFAULT_ERROR_MESSAGE,
        required: true,
      },
      email: {
        value: studentDetails?.email || '',
        validator: (email) => emailValidator(email),
        errorMessage: null,
        required: true,
      },
      phoneNumber: {
        value: studentDetails?.phoneNumber || '',
        validator: (phone) => phoneAndPrefixValidator(phone),
        errorMessage: null,
        required: true,
      },
      postcode: {
        value: studentDetails?.postcode || '',
        validator: (postcode) => postcode && postcode.length > 3 && postcode.length <= 20,
        errorMessage: DEFAULT_ERROR_MESSAGE,
        required: true,
      },
      countryOfResidence: {
        value: studentDetails?.countryOfResidence || '',
        validator: Boolean,
        errorMessage: DEFAULT_ERROR_MESSAGE,
        required: true,
      },
      dateOfBirth: {
        value: studentDetails?.dateOfBirth || '',
        validator: (birthDate: string) => {
          const birthDateTime = new Date(birthDate).getTime();
          return birthDateTime < this.calendarMaxDate.getTime() && birthDateTime >= this.calendarMinDate.getTime();
        },
        errorMessage: DEFAULT_ERROR_MESSAGE,
        required: true,
      },
    };

    if (this.withNationalityField) {
      this.formFields = {
        ...this.formFields,
        nationality: {
          value: studentDetails?.nationality || '',
          validator: Boolean,
          errorMessage: DEFAULT_ERROR_MESSAGE,
          required: true,
        },
      };
    }
  }

  private isFormValid(): boolean {
    const formFields = Object.values(this.formData);
    if (formFields.some((formField) => formField.isFieldRequiredAndPristineAndEmpty())) {
      return false;
    }

    formFields.filter((field) => field.isValid === undefined).forEach((field) => field.validate(field.isDirty));
    return formFields.every((field) => field.isValid);
  }

  /**
   * Initialize phone number fields and date of birth from student details to be able
   * to preselect those fields on form
   */
  private initializeDataFromStoredStudentDetails(studentDetails: StudentDetails) {
    const phoneNumber = studentDetails?.phoneNumber || '';
    this.phoneCode = splitPhoneDigits(phoneNumber, 0);
    this.phoneNumber = splitPhoneDigits(phoneNumber, 1);
    this.preselectedDateOfBirth = getDateFromString(studentDetails?.dateOfBirth);
  }
}
